import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  Engineering as EngineeringIcon,
  Assignment as VhcIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import api from '../services/api';

const EditBookingModal = ({ open, onClose, booking, onSave }) => {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = ['admin', 'org_admin', 'garage_manager'].includes(user?.role) || user?.is_superuser;

  const [bookingDate, setBookingDate] = useState(null);
  const [timeOfDay, setTimeOfDay] = useState('');
  const [assignedTechnicianId, setAssignedTechnicianId] = useState('');
  const [vhcStatus, setVhcStatus] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (open && booking) {
      // Initialize form with booking data
      setBookingDate(booking.booking_date ? new Date(booking.booking_date) : null);
      setTimeOfDay(booking.time_of_day || '');
      setAssignedTechnicianId(booking.assigned_technician_id || '');
      setVhcStatus(booking.health_check?.status || '');
      setError('');
      setSuccess('');

      // Fetch technicians list
      fetchTechnicians();
    }
  }, [open, booking]);

  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/v1/technicians/');
      setTechnicians(response.data || []);
    } catch (err) {
      console.error('Failed to fetch technicians:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        booking_date: bookingDate ? bookingDate.toISOString().split('T')[0] : null,
        time_of_day: timeOfDay,
        assigned_technician_id: assignedTechnicianId || null,
      };

      const response = await api.patch(`/v1/bookings/${booking.id}/update/`, updateData);

      // Update VHC status if changed (admin only)
      if (isAdmin && booking.health_check && vhcStatus !== booking.health_check.status) {
        await api.patch(`/v1/evhc/${booking.health_check.id}/`, { status: vhcStatus });
      }

      setSuccess('Booking updated successfully');

      // Notify parent to refresh data
      if (onSave) {
        onSave(response.data.booking);
      }

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update booking');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = () => {
    setConfirmDialogOpen(false);
    handleSave();
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Booking</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Booking Info (Read-only) */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2">
              <strong>{booking.customer_name}</strong>
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <CarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              {booking.vehicle_info}
            </Typography>
          </Box>
        </Box>

        {/* Editable Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Booking Date */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Booking Date"
              value={bookingDate}
              onChange={(newValue) => setBookingDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputProps: {
                    startAdornment: (
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    ),
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* Time of Day */}
          <FormControl fullWidth>
            <InputLabel>Time of Day</InputLabel>
            <Select
              value={timeOfDay}
              label="Time of Day"
              onChange={(e) => setTimeOfDay(e.target.value)}
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              <MenuItem value="morning">Morning</MenuItem>
              <MenuItem value="afternoon">Afternoon</MenuItem>
              <MenuItem value="all_day">All Day</MenuItem>
            </Select>
          </FormControl>

          {/* Assigned Technician */}
          <FormControl fullWidth>
            <InputLabel>Assigned Technician</InputLabel>
            <Select
              value={assignedTechnicianId}
              label="Assigned Technician"
              onChange={(e) => setAssignedTechnicianId(e.target.value)}
              startAdornment={
                <EngineeringIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {technicians.map((tech) => (
                <MenuItem key={tech.id} value={tech.id}>
                  {tech.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* VHC Section (Admin only, only if VHC exists) */}
        {isAdmin && booking.health_check && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <VhcIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold">
                Vehicle Health Check
              </Typography>
            </Box>

            {/* Status counts: Red/Amber/Green */}
            <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
              <Chip
                label={`${booking.health_check.red_count} Red`}
                size="small"
                sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}
              />
              <Chip
                label={`${booking.health_check.amber_count} Amber`}
                size="small"
                sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}
              />
              <Chip
                label={`${booking.health_check.green_count} Green`}
                size="small"
                sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
              />
            </Box>

            {/* Status dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel>VHC Status</InputLabel>
              <Select
                value={vhcStatus}
                label="VHC Status"
                onChange={(e) => setVhcStatus(e.target.value)}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="reviewed">Reviewed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveClick}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography>
            All changes will be recorded for audit purposes. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditBookingModal;
